import { PlaceholderShimmerBackground, Stretch } from 'utils/fragments';
import { useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const ImageContainer = styled.picture`
    position: relative;
    background-color: ${({ theme }) => theme.gray_5};
    min-width: 100%;
`;

const Image = styled.img`
    border: 0;
    ${Stretch};
    height: 100%;
    width: 100%;
    object-fit: cover;
    object-position: ${({ $position }) => $position};
    opacity: ${({ isLoading }) => +!isLoading};
`;

export const ImageOverlay = styled.div.attrs(({ shimmerWidth = '5000px' }) => ({
    shimmerWidth,
}))`
    ${Stretch};
    ${({ isLoading }) => (isLoading ? PlaceholderShimmerBackground : null)}
`;

function Picture({
    src,
    alt,
    isDisabled,
    isLoading,
    $position = '50% 50%',
    ...props
}) {
    const imageRef = useRef(null);
    const [{ 0: key, 1: status }, setStatus] = useState([
        src,
        src ? 'loading' : 'init',
    ]);

    if (src !== key) setStatus([src, src ? 'loading' : 'init']);

    useLayoutEffect(() => {
        const image = imageRef.current;
        if (!image?.complete) return;
        // A cached image finished before React attached these handlers, so the
        // event they were waiting for has already fired and will not fire again.
        // No intrinsic size means it finished by failing.
        const settled =
            image.naturalWidth > 0 || image.naturalHeight > 0
                ? 'loaded'
                : 'error';
        setStatus((previous) =>
            previous[0] === src && previous[1] === 'loading'
                ? [src, settled]
                : previous,
        );
    }, [src]);

    const imageIsLoading = status === 'loading';

    return (
        <ImageContainer status={status} {...props}>
            {src && !(key === src && status === 'error') ? (
                <Image
                    ref={imageRef}
                    src={src}
                    alt={alt}
                    isLoading={imageIsLoading}
                    onLoad={() => setStatus([src, 'loaded'])}
                    onError={() => setStatus([src, 'error'])}
                    $position={$position}
                />
            ) : null}
            {!isDisabled || imageIsLoading || isLoading ? (
                <ImageOverlay isLoading={imageIsLoading || isLoading} />
            ) : null}
        </ImageContainer>
    );
}

export default Picture;
